//
// This file is the starting point for the client side Javascript
//
import '@sass/app.scss'
import '@morev/vue-transitions/styles'

import { createApp, h } from 'vue'
import { createInertiaApp, router } from '@inertiajs/vue3'
import translationsPlugin, { trans } from '@js/App/Plugins/Translations'
import localizedUrlsPlugin from '@js/App/Plugins/LocalizedUrls'
import formatNumberPlugin from '@js/App/Plugins/Formatting'
import modalsPlugin from '@js/App/Plugins/Modals'
import { addTrackingScriptsIfProduction } from '@js/App/Performance/LazyScripts'
import { createPinia } from 'pinia'
import { setupPageViewTracking } from '@js/App/Tracking/Tracking'
import { setupResetStoresOnNavigation } from '@js/App/Stores/StoresSetup'
import VueTippy from 'vue-tippy'
import Layout from '@js/Pages/Layout.vue'
import VueTransitions from '@morev/vue-transitions'
import * as Sentry from '@sentry/vue'
import { thirdPartyErrorFilterIntegration } from '@sentry/vue'
import { isCancel } from 'axios'
import { getFromCache, putInCache } from '@js/App/Performance/LocalCache'
import { isSSR } from '@js/App/Utils/Environment'
import SSRLayout from '@js/Pages/SSRLayout.vue'
import { usePostHog } from './composables/usePostHog'

const onAppLoadedAndReady = () => {
  addTrackingScriptsIfProduction()
  setupPageViewTracking()
  setupResetStoresOnNavigation()

  router.on('navigate', () => {
    console.log('[DEBUG] Tracking history')
    const history = getFromCache('debug_track_history') || []

    history.push({
      url: window.location.href,
      date: new Date()
    })

    putInCache('debug_track_history', history)
  })
}

if (!isSSR()) {
  // This snipped makes sure that when clicking the back button we reload components and
  // don't use the cached state.
  window.addEventListener('popstate', (event) => {
    event.stopImmediatePropagation()

    router.reload({
      preserveState: false,
      preserveScroll: false,
      replace: true
    })
  })
}

createInertiaApp({
  progress: {
    delay: 0,
    color: '#E71C77',
    includeCSS: true,
    showSpinner: false
  },
  title: title => title || trans('Layout.MetaTitle'),
  resolve: (name) => {
    const pages = import.meta.glob('./Pages/**/*.vue')
    const page = pages[`./Pages/${name}.vue`]
    if (!page) {
      window.location.reload()
      return new Promise(() => {})
    }
    return page().then((module) => {
      sessionStorage.removeItem('chunk_reload')
      if (!module.default.layout) {
        module.default.layout = (h, page) => h(SSRLayout, () => h(Layout, () => page))
      }
      return module
    }).catch(() => {
      if (!sessionStorage.getItem('chunk_reload')) {
        sessionStorage.setItem('chunk_reload', '1')
        window.location.reload()
        return new Promise(() => {})
      }
      sessionStorage.removeItem('chunk_reload')
    })
  },
  setup ({ el, App, props, plugin }) {
    const pinia = createPinia()

    const app = createApp({ render: () => h(App, props) })
      .use(plugin)
      .use(pinia)
      .use(translationsPlugin)
      .use(localizedUrlsPlugin)
      .use(formatNumberPlugin)
      .use(modalsPlugin)
      .use(VueTippy)
      .use(VueTransitions)

    Sentry.init({
      app,
      dsn: 'https://f6fac2680cf633235fe0ef3d438d5553@o1346347.ingest.us.sentry.io/4506359496245248',
      enabled: props.initialPage.props.environment === 'production',
      tracesSampleRate: 0.5,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      integrations: [
        thirdPartyErrorFilterIntegration({
          filterKeys: ['rentbird-frontend'],
          behaviour: 'drop-error-if-exclusively-contains-third-party-frames'
        })
      ],
      denyUrls: [
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
        /extensions\//i,
        /^webkit-masked-url:\/\//,
        // Meta's in-app browser performance logger, injected into our page and throwing from
        // its own pagehide/beforeunload hooks. Android names its frames iabjs://.
        /^iabjs:\/\//i,
        // PostHog's lazily loaded session recorder throws a cross-origin SecurityError when it
        // reads from Stripe.js's hidden fraud-detection iframe. The browser blocks the read, so
        // no fix is possible in our code. Recordings keep working.
        /\/static\/posthog-recorder\.js/
      ],
      // The iOS variant of the same logger is injected inline, so its frames carry our page
      // URL rather than a third-party one - which is why thirdPartyErrorFilterIntegration
      // above does not drop it and denyUrls cannot reach it. Match on the message instead.
      ignoreErrors: [
        /Error invoking postMessage/i,
        /window\.webkit\.messageHandlers/i
      ],
      beforeSend (event, hint) {
        const error = hint.originalException
        if (
          error?.code === 'ECONNABORTED' ||
          error?.code === 'ERR_NETWORK' ||
          isCancel(error)
        ) {
          return null
        }
        return event
      }
    })

    // Our own Cookiebot consent scanner self-grants marketing consent so it can
    // enumerate our cookies, which would otherwise make every URL it crawls a
    // separate PostHog person. Only the server can tell it apart from a real
    // visitor — from here its user agent is an ordinary Chrome.
    if (props.initialPage.props.environment === 'production' && !props.initialPage.props.isConsentScanner) {
      usePostHog(props.initialPage.props.user?.random_id)
    }

    app.mount(el)

    onAppLoadedAndReady()
  }
})
